/**
 * Small add/edit modals shared by the Concept Unit / Level rows in both
 * MilestoneSections.jsx (DAPC Design node) and DesignSection.jsx (Reference >
 * Design > Concepts). Kept in their own file so neither component file has to
 * import from the other (which would create a circular import).
 */
import { useState } from 'react'
import { createPortal } from 'react-dom'
import { UNIT_TYPE_OPTIONS } from '../outline/constants.js'

export function UnitTypeModal({ title, initial, confirmLabel, onSave, onClose }) {
  const [unitType, setUnitType] = useState(initial || '')
  const [saving, setSaving] = useState(false)

  async function handleSave() {
    if (!unitType) return
    setSaving(true)
    try {
      await onSave(unitType)
      onClose()
    } catch {
      setSaving(false)
    }
  }

  return createPortal(
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 900, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}
    >
      <div
        style={{ background: 'var(--aj-bg-elevated)', borderRadius: 10, padding: '28px 32px', width: 360, maxWidth: '90vw', display: 'flex', flexDirection: 'column', gap: 18 }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ fontFamily: 'Montserrat,sans-serif', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--aj-text)' }}>
          {title}
        </div>
        <select
          value={unitType}
          onChange={e => setUnitType(e.target.value)}
          style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '8px 10px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'var(--aj-bg-subtle)', color: 'var(--aj-text)', outline: 'none' }}
        >
          <option value="">Select unit type…</option>
          {UNIT_TYPE_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 8, borderTop: '0.5px solid var(--aj-border)', marginTop: 4 }}>
          <button onClick={onClose} style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '7px 16px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'transparent', color: 'var(--aj-text)', cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={!unitType || saving} style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '7px 16px', border: 'none', borderRadius: 4, background: '#07272D', color: '#fff', cursor: (!unitType || saving) ? 'default' : 'pointer', opacity: (!unitType || saving) ? 0.6 : 1 }}>
            {saving ? 'Saving…' : (confirmLabel || 'Save')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

export function LevelRenameModal({ initial, onSave, onClose }) {
  const [name, setName] = useState(initial || '')
  const [saving, setSaving] = useState(false)

  async function handleSave() {
    if (!name.trim()) return
    setSaving(true)
    try {
      await onSave(name.trim())
      onClose()
    } catch {
      setSaving(false)
    }
  }

  return createPortal(
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 900, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}
    >
      <div
        style={{ background: 'var(--aj-bg-elevated)', borderRadius: 10, padding: '28px 32px', width: 360, maxWidth: '90vw', display: 'flex', flexDirection: 'column', gap: 16 }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ fontFamily: 'Montserrat,sans-serif', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--aj-text)' }}>
          Edit Level Name
        </div>
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '8px 10px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'var(--aj-bg-subtle)', color: 'var(--aj-text)', outline: 'none', boxSizing: 'border-box' }}
        />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 8, borderTop: '0.5px solid var(--aj-border)', marginTop: 4 }}>
          <button onClick={onClose} style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '7px 16px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'transparent', color: 'var(--aj-text)', cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={!name.trim() || saving} style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '7px 16px', border: 'none', borderRadius: 4, background: '#07272D', color: '#fff', cursor: (!name.trim() || saving) ? 'default' : 'pointer', opacity: (!name.trim() || saving) ? 0.6 : 1 }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

export function AddLevelModal({ defaultName, defaultOrder, onSave, onClose }) {
  const [name, setName] = useState(defaultName)
  const [order, setOrder] = useState(defaultOrder)
  const [saving, setSaving] = useState(false)

  const inp = {
    fontFamily: '"DM Sans",sans-serif', fontSize: 12,
    padding: '8px 10px', border: '0.5px solid var(--aj-border-mid)',
    borderRadius: 4, background: 'var(--aj-bg-subtle)', color: 'var(--aj-text)',
    outline: 'none', boxSizing: 'border-box', width: '100%',
  }

  async function handleSave() {
    if (!name.trim()) return
    setSaving(true)
    try {
      await onSave({ Name: name.trim(), 'Stacking Order': order })
      onClose()
    } catch {
      setSaving(false)
    }
  }

  return createPortal(
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 900, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}
    >
      <div
        style={{ background: 'var(--aj-bg-elevated)', borderRadius: 10, padding: '28px 32px', width: 360, maxWidth: '90vw', display: 'flex', flexDirection: 'column', gap: 18 }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ fontFamily: 'Montserrat,sans-serif', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--aj-text)' }}>
          Add Level
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-mid)' }}>Name</label>
          <input type="text" value={name} onChange={e => setName(e.target.value)} style={inp} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-mid)' }}>Stacking Order</label>
          <input type="number" value={order} onChange={e => setOrder(e.target.value === '' ? null : Number(e.target.value))} style={inp} />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 8, borderTop: '0.5px solid var(--aj-border)', marginTop: 4 }}>
          <button onClick={onClose} style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '7px 16px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'transparent', color: 'var(--aj-text)', cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={!name.trim() || saving} style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '7px 16px', border: 'none', borderRadius: 4, background: '#07272D', color: '#fff', cursor: (!name.trim() || saving) ? 'default' : 'pointer', opacity: (!name.trim() || saving) ? 0.6 : 1 }}>
            {saving ? 'Adding…' : 'Add'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
